// What the kit needs from the board, and the arithmetic that keeps a settled
// camera from re-rendering the interface sixty times a second.
//
// **Zero imports, for a narrower reason than `tokens.ts`'s.** The port below
// is the shape `BoardCanvasHandle` already has; declaring it *structurally*
// means the kit never imports `BoardCanvas.tsx` — a file on the `@mui`
// burn-down list, which would drag the whole shell in behind one type.
// TypeScript checks the handle against this by shape, so the two cannot drift
// without `useBoardPoint`'s call sites failing to compile.

/** A point in viewport pixels — what `projectSquare` and `projectCentre` answer. */
export interface Point {
  x: number;
  y: number;
}

/**
 * The board, as much of it as an anchored overlay needs.
 *
 * **`projectSquare` is the square's screen CORNER and `projectCentre` its
 * CENTRE** — a name swap that is a fact about `BoardCanvasHandle`, not a
 * choice made here. Badges hang off the corner; a label naming the square
 * wants its middle. Both are repeated here so the pair travels together.
 */
export interface BoardAnchorPort {
  /**
   * Run after each animation frame, so an overlay projects against the camera
   * the frame on screen was drawn with. Returns an unsubscribe.
   */
  onFrame(listener: () => void): () => void;
  /** The square's centre. */
  projectCentre(square: number): Point;
  /** The square's corner — where the badges hang. */
  projectSquare(square: number): Point;
}

/**
 * Are these the same point, to within half a pixel?
 *
 * **The whole reason anchoring is cheap.** The frame callback runs on every
 * frame the renderer draws, and a camera easing to rest is dozens of frames of
 * sub-pixel movement — an unconditional `setState` re-renders every consumer
 * with numbers no rounded `left` can tell apart. Half a pixel is below what
 * the screen can show.
 *
 * Null is a point in its own right — "there is nothing to anchor to" — and two
 * nulls are the same one.
 */
export function samePoint(a: Point | null, b: Point | null, eps = 0.5): boolean {
  if (a === null || b === null) return a === b;
  return Math.abs(a.x - b.x) < eps && Math.abs(a.y - b.y) < eps;
}
